'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { SSOUser } from '@sso/auth-client';

import { UI_CONDITION_KEYS } from '@workspace/lib/constants/ui-conditions-keys';
import { useVisibilityRules } from '@workspace/lib/hooks/common/use-visibility-rules';

interface AuthContextValue {
  user: SSOUser | null;
  isAuthenticated: boolean;
  /** Forgets the user locally, e.g. when the server no longer sees a session. */
  clearUser: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  clearUser: () => {},
});

interface AuthProviderProps {
  initialUser: SSOUser | null;
  children: React.ReactNode;
}

export function AuthProvider({ initialUser, children }: AuthProviderProps) {
  const { setVisibilityRule } = useVisibilityRules();
  const [user, setUser] = useState(initialUser);

  // The server's user wins whenever it sends a new one (e.g. after a refresh).
  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  // Re-sync on every mount so a stale persisted rule from a prior session is overwritten.
  useEffect(() => {
    setVisibilityRule(UI_CONDITION_KEYS.IS_AUTH, !!user);
  }, [user, setVisibilityRule]);

  const clearUser = useCallback(() => setUser(null), []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, clearUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
